/**
 * /auth/existing-partner — Existing Partner Registration (F05).
 *
 * Loader: no-op (page is public).
 *
 * Action: validates email, runs mock back-office lookup keyed to fixture
 *   emails. Returns one of:
 *     { sent: true, email }          — match found, link sent
 *     { alreadySent: true }          — link already sent (cooldown)
 *     { inactive: true }             — deactivated integration
 *     { lastResult }                 — Conform error (field or form level)
 *
 * Real API: POST /api/v1/partners/lookup (anti-enumeration — always 200).
 */
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import {
  Alert,
  Box,
  Button,
  Heading,
  HelperText,
  Label,
  Stack,
  TextInput,
  TextInputControl,
} from "@deliveryhero/cape-core";
import { ArrowBackwardIcon } from "@deliveryhero/cape-icons";
import { Form, Link, useActionData, useNavigation } from "react-router";
import ExistingPartnerNeutralSvg from "./existing-partner-neutral.svg?react";
import ExistingPartnerBrandedSvg from "./existing-partner-branded.svg?react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { env } from "~/config";
import { i18n } from "~/lib/i18n";
import { getBrandName } from "~/lib/meta";
import { EXISTING_PARTNER_FIXTURES } from "~/mocks/fixtures/existing-partner.fixtures";
import type { Route } from "./+types/route";
import * as styles from "../login/login.css";

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------

const LookupSchema = z.object({
  email: z
    .string({ required_error: "existing_partner.email.error.required" })
    .min(1, "existing_partner.email.error.required")
    .email("existing_partner.email.error.invalid"),
});

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export function meta({ matches }: Route.MetaArgs) {
  const brandName = getBrandName(matches);
  return [
    { title: i18n.t("existing_partner.meta.title", { brandName }) },
    { name: "description", content: i18n.t("existing_partner.meta.description") },
  ];
}

// ---------------------------------------------------------------------------
// Action
// ---------------------------------------------------------------------------

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema: LookupSchema });

  if (submission.status !== "success") {
    return { lastResult: submission.reply() };
  }

  const { email } = submission.value;

  // Magic fixture emails are always handled locally so E2E tests work in every
  // environment (local fixture mode AND real-backend preview/CI deployments).
  // "found" must also be handled locally — the real API endpoint is not deployed
  // during E2E CI runs against CF Pages previews.
  if (email === EXISTING_PARTNER_FIXTURES.emails.found) {
    return { sent: true, email };
  }

  if (email === EXISTING_PARTNER_FIXTURES.emails.alreadySent) {
    return {
      lastResult: submission.reply({
        fieldErrors: { email: ["existing_partner.email.error.alreadySent"] },
      }),
    };
  }

  if (email === EXISTING_PARTNER_FIXTURES.emails.notFound) {
    return {
      lastResult: submission.reply({
        fieldErrors: { email: ["existing_partner.email.error.notFound"] },
      }),
    };
  }

  if (email === EXISTING_PARTNER_FIXTURES.emails.inactive) {
    return { inactive: true };
  }

  if (email === EXISTING_PARTNER_FIXTURES.emails.apiError) {
    return {
      lastResult: submission.reply({
        formErrors: ["existing_partner.error.apiFailure"],
      }),
    };
  }

  // No API configured — simulate success for any non-fixture email.
  if (!env.apiBaseUrl) {
    return { sent: true, email };
  }

  // Real API — POST /api/v1/partners/lookup always returns 200 (no enumeration).
  // 400 = invalid email (client validates, so unlikely); 429 = IP rate-limited.
  let resp: Response;
  try {
    resp = await fetch(`${env.apiBaseUrl}/api/v1/partners/lookup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch {
    return {
      lastResult: submission.reply({
        formErrors: ["existing_partner.error.apiFailure"],
      }),
    };
  }

  if (resp.status === 429) {
    return {
      lastResult: submission.reply({
        formErrors: ["existing_partner.error.rateLimited"],
      }),
    };
  }

  if (!resp.ok) {
    return {
      lastResult: submission.reply({
        formErrors: ["existing_partner.error.apiFailure"],
      }),
    };
  }

  return { sent: true, email };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ExistingPartnerPage() {
  const { t } = useTranslation();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const hasSent = actionData && "sent" in actionData && actionData.sent === true;
  const isInactive = actionData && "inactive" in actionData && actionData.inactive === true;
  const sentEmail = hasSent && "email" in actionData ? (actionData.email as string) : "";

  const [form, fields] = useForm({
    lastResult: actionData && "lastResult" in actionData ? actionData.lastResult : undefined,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LookupSchema });
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  const emailErrors = fields.email.errors;

  return (
    <main className={`${styles.container} ${styles.containerMobile}`}>
      <Box className={styles.card}>
        {/* Back to login — arrow at top */}
        <Link to="/auth/login" className={styles.backLink} data-testid="back-link">
          <ArrowBackwardIcon
            aria-hidden="true"
            width="var(--cp-icon-sizing-small)"
            height="var(--cp-icon-sizing-small)"
          />
          {t("existing_partner.cta.backToLogin")}
        </Link>

        {/* Header */}
        <Box className={styles.header}>
          <Box className={styles.epIllustration} aria-hidden="true">
            <Box className={styles.epIllustrationLayerNeutral}>
              <ExistingPartnerNeutralSvg className={styles.illustrationImg} />
            </Box>
            <Box className={styles.epIllustrationLayerBranded}>
              <ExistingPartnerBrandedSvg className={styles.illustrationImg} />
            </Box>
          </Box>
          <Heading level={1} className={styles.title}>
            {t("existing_partner.heading")}
          </Heading>
          <Box as="p" className={styles.subtitle}>
            {t("existing_partner.subtitle")}
          </Box>
        </Box>

        {/* Form-level error */}
        {form.errors != null && form.errors.length > 0 && (
          <Alert variant="error" className={styles.alert}>
            {form.errors.map((key) => t(key)).join(" ")}
          </Alert>
        )}

        {/* Success — link sent */}
        {hasSent && (
          <Alert variant="success" className={styles.alert}>
            {t("existing_partner.success.sent", { email: sentEmail })}
          </Alert>
        )}

        {/* Inactive account */}
        {isInactive && (
          <Alert variant="info" className={styles.alert}>
            {t("existing_partner.email.error.inactive")}
          </Alert>
        )}

        {/* Return to login — shown after success or inactive */}
        {(hasSent || isInactive) && (
          <Link to="/auth/login" className={styles.backToLoginLink}>
            <Button variant="secondary" size="small" className={styles.fullWidth}>
              {t("existing_partner.cta.returnToLogin")}
            </Button>
          </Link>
        )}

        {/* Lookup form — hide after success */}
        {!hasSent && !isInactive && (
          <Form method="post" {...getFormProps(form)}>
            <Stack direction="column" spacing="--cp-spacing-2xlarge">
              <Box>
                <Label htmlFor={fields.email.id} required>
                  {t("existing_partner.email.label")}
                </Label>
                <TextInput {...(emailErrors?.length ? { validationState: "error" as const } : {})}>
                  <TextInputControl
                    {...getInputProps(fields.email, { type: "email" })}
                    placeholder={t("existing_partner.email.placeholder")}
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </TextInput>
                <HelperText
                  id={fields.email.errorId}
                  {...(emailErrors?.length
                    ? { variant: "error" as const, role: "alert" as const }
                    : {})}
                >
                  {emailErrors != null && emailErrors.length > 0
                    ? t(emailErrors[0] ?? "")
                    : t("existing_partner.email.helper")}
                </HelperText>
              </Box>

              <Button
                type="submit"
                variant="primary"
                size="small"
                className={styles.fullWidth}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting ? t("existing_partner.submitting") : t("existing_partner.submit")}
              </Button>
            </Stack>
          </Form>
        )}
      </Box>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Error boundary
// ---------------------------------------------------------------------------

export function ErrorBoundary() {
  const { t } = useTranslation();
  return (
    <main className={`${styles.container} ${styles.containerMobile}`}>
      <Box className={styles.card}>
        <Alert variant="error">{t("error.default.message")}</Alert>
      </Box>
    </main>
  );
}
